import {
    AlertTriangle,
    ArrowRight,
    BadgeIndianRupee,
    CalendarClock,
    ChevronLeft,
    CirclePlus,
    LoaderCircle,
    Sparkles,
    TrendingDown,
    TrendingUp,
    Users,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './PayrollRunHistoryScreen.module.css'

const STATUS_CONFIG = {
    DRAFT: { label: 'Draft', color: '#E65100', bg: '#FFF3E0' },
    REVIEW: { label: 'In Review', color: '#1565C0', bg: '#E3F2FD' },
    FINALIZED: { label: 'Finalized', color: '#2E7D32', bg: '#E8F5E9' },
}

const PAGE_SIZE = 12

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatINR = (amount, decimals = 0) => {
    const value = Number(amount)
    if (!Number.isFinite(value)) return '—'
    return value.toLocaleString('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })
}

const formatDate = (value) => {
    if (!value) return '—'
    const parsed = String(value).includes(' ') ? String(value).replace(' ', 'T') : String(value)
    const date = new Date(parsed)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    })
}

const PayrollRunHistoryScreen = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [historyData, setHistoryData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)

    const runs = historyData?.runs || []
    const pagination = historyData?.pagination || null
    const summary = historyData?.summary || null

    const fetchHistory = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            setIsLoading(false)
            return
        }

        try {
            setIsLoading(true)

            const response = await fetch(BASE_URL_API + 'getPayrollRunHistory', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    page: currentPage,
                    pageSize: PAGE_SIZE,
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setHistoryData(respo.response)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            toast.error(respo?.message || 'Failed to load payroll history.')
        } catch {
            toast.error('Network error.')
        } finally {
            setIsLoading(false)
        }
    }, [activeStore?.storeIdentifier, authCtx, currentPage])

    useEffect(() => {
        fetchHistory()
    }, [fetchHistory])

    useEffect(() => {
        const onFocus = () => {
            fetchHistory()
        }

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') onFocus()
        }

        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibilityChange)

        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    }, [fetchHistory])

    const openRunPayroll = useCallback(() => {
        const now = new Date()
        const year = now.getFullYear()
        const month = now.getMonth() + 1

        const existingDraft = runs.find(
            (run) =>
                String(run?.runStatus || '').toUpperCase() === 'DRAFT' &&
                Number(run?.cycleYear) === year &&
                Number(run?.cycleMonth) === month
        )

        if (existingDraft?.runIdentifier) {
            navigate(`/payroll/run/${existingDraft.runIdentifier}`)
            return
        }

        navigate('/payroll/run')
    }, [navigate, runs])

    const handleRunPress = (run) => {
        if (!run?.runIdentifier) return
        navigate(`/payroll/run/${run.runIdentifier}`)
    }

    const hasHistory = runs.length > 0

    const summaryChips = useMemo(() => {
        if (!summary) return []
        return [
            { label: `${Number(summary?.finalizedCount || 0)} finalized`, tone: 'green' },
            { label: `Avg payout: ₹${formatINR(summary?.avgNetPayoutFinalized || 0, 2)}`, tone: 'blue' },
            { label: `${Number(summary?.draftCount || 0)} draft`, tone: Number(summary?.draftCount || 0) > 0 ? 'amber' : 'gray' },
        ]
    }, [summary])

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button className={styles.backBtn} type="button" onClick={() => navigate(-1)}>
                    <ChevronLeft size={18} />
                </button>

                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}>
                        <CalendarClock size={12} /> Payroll
                    </span>
                    <h1 className={styles.headerTitle}>Payroll History</h1>
                    <p className={styles.headerSub}>Browse previous payroll cycles and open run details.</p>
                </div>

                <div className={styles.headerActions}>
                    <button type="button" className={styles.secondaryBtn} onClick={() => navigate('/payroll/simulator')}>
                        <Sparkles size={14} /> Simulate Changes
                    </button>
                    <button type="button" className={styles.primaryBtn} onClick={openRunPayroll}>
                        <CirclePlus size={14} /> Run Payroll
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div className={styles.loaderCard}>
                    <LoaderCircle size={20} className={styles.spinIcon} />
                    <span>Loading payroll history...</span>
                </div>
            ) : !hasHistory ? (
                <section className={styles.emptyStateCard}>
                    <h3>No payroll runs yet.</h3>
                    <p>Run your first payroll cycle once setup and staff assignment are complete.</p>
                    <button type="button" className={styles.primaryBtn} onClick={openRunPayroll}>
                        Run First Payroll <ArrowRight size={14} />
                    </button>
                </section>
            ) : (
                <>
                    {summaryChips.length > 0 ? (
                        <section className={styles.summaryStrip}>
                            {summaryChips.map((chip) => (
                                <span key={chip.label} className={`${styles.summaryChip} ${styles[`chip${chip.tone[0].toUpperCase()}${chip.tone.slice(1)}`]}`}>
                                    {chip.label}
                                </span>
                            ))}
                        </section>
                    ) : null}

                    <section className={styles.listWrap}>
                        {runs.map((run) => (
                            <PayrollRunCard key={run.runIdentifier} run={run} onPress={handleRunPress} />
                        ))}
                    </section>

                    {pagination?.totalPages > 1 ? (
                        <section className={styles.paginationRow}>
                            <button
                                type="button"
                                className={styles.secondaryBtn}
                                onClick={() => setCurrentPage((page) => page - 1)}
                                disabled={!pagination?.hasPrevPage}
                            >
                                ← Prev
                            </button>
                            <span className={styles.pageInfo}>
                                Page {pagination?.page} of {pagination?.totalPages}
                            </span>
                            <button
                                type="button"
                                className={styles.secondaryBtn}
                                onClick={() => setCurrentPage((page) => page + 1)}
                                disabled={!pagination?.hasNextPage}
                            >
                                Next →
                            </button>
                        </section>
                    ) : null}
                </>
            )}
        </div>
    )
}

const PayrollRunCard = ({ run, onPress }) => {
    const statusCfg = STATUS_CONFIG[String(run?.runStatus || '').toUpperCase()] || {
        label: String(run?.runStatus || 'Run'),
        color: '#475569',
        bg: '#E2E8F0',
    }

    const delta = Number(run?.deltaVsLastCycle)
    const hasDelta = Number.isFinite(delta)
    const hasIncrease = hasDelta && delta > 0
    const hasDecrease = hasDelta && delta < 0
    const noChange = hasDelta && delta === 0

    return (
        <button type="button" className={styles.card} onClick={() => onPress(run)}>
            <div className={styles.cardHeader}>
                <div>
                    <strong className={styles.cycleLabel}>{run?.cycleLabel || 'Payroll Run'}</strong>
                    <span className={styles.cycleSub}>
                        {run?.cycleStartDate || '—'} to {run?.cycleEndDate || '—'}
                    </span>
                </div>
                <span className={styles.statusBadge} style={{ color: statusCfg.color, backgroundColor: statusCfg.bg }}>
                    {statusCfg.label}
                </span>
            </div>

            <div className={styles.figuresRow}>
                <div className={styles.figureItem}>
                    <BadgeIndianRupee size={14} />
                    <div>
                        <strong>₹{formatINR(run?.totalNetPayout || 0)}</strong>
                        <span>Net Payout</span>
                    </div>
                </div>
                <div className={styles.figureItem}>
                    <Users size={14} />
                    <div>
                        <strong>{Number(run?.totalStaffCount || 0)}</strong>
                        <span>Staff</span>
                    </div>
                </div>
            </div>

            {hasDelta ? (
                <div className={styles.deltaRow}>
                    {hasIncrease ? (
                        <span className={styles.deltaUp}>
                            <TrendingUp size={13} /> +₹{formatINR(delta)}
                            {run?.deltaPercent !== null && run?.deltaPercent !== undefined
                                ? ` (+${formatINR(run.deltaPercent, 2)}% vs last month)`
                                : ''}
                        </span>
                    ) : null}

                    {hasDecrease ? (
                        <span className={styles.deltaDown}>
                            <TrendingDown size={13} /> −₹{formatINR(Math.abs(delta))}
                            {run?.deltaPercent !== null && run?.deltaPercent !== undefined
                                ? ` (${formatINR(run.deltaPercent, 2)}% vs last month)`
                                : ''}
                        </span>
                    ) : null}

                    {noChange ? <span className={styles.deltaNeutral}>— No change vs last month</span> : null}
                </div>
            ) : null}

            {Number(run?.pendingAttendanceCount || 0) > 0 ? (
                <div className={styles.pendingWarning}>
                    <AlertTriangle size={14} />
                    <span>{Number(run?.pendingAttendanceCount)} unaccepted attendance</span>
                </div>
            ) : null}

            <div className={styles.cardFoot}>
                {run?.finalizedOn ? (
                    <span className={styles.finalizedOn}>Finalized {formatDate(run.finalizedOn)}</span>
                ) : (
                    <span className={styles.finalizedOn}>Created {formatDate(run?.createdOn)}</span>
                )}
                <span className={styles.openHint}>
                    Open <ArrowRight size={13} />
                </span>
            </div>
        </button>
    )
}

export default PayrollRunHistoryScreen